/**
 * Generates a lexicographically ordered string-based index between two existing indices.
 * Uses a restricted character set (0-9A-Z_a-z) to generate an index that sorts between
 * the given 'before' and 'after' indices.
 *
 * @param {string} before - The index string that comes before the desired new index
 * @param {string} after - The index string that comes after the desired new index
 * @returns {string} A new string that sorts lexicographically between before and after
 * @throws {Error} If before is greater than or equal to after (except when equal)
 */
export function getFractionalIndex(before = '', after = ''): string {
  // Define valid characters in order: 0-9, A-Z, _, a-z

  // If both strings are empty, return middle of valid range
  if (!(before || after)) {
    return 'A'
  }

  // If the strings are identical, append '0'
  if (before === after) {
    return `${before}0`
  }

  // Validate that before is less than after (when they're not equal)
  if (before && after && before > after) {
    throw new Error('before index must be less than after index')
  }

  // If before string is empty, return character before 'after'
  if (!before) {
    const afterFirstChar = after[0]
    const afterIndex = VALID_CHARS.indexOf(afterFirstChar)

    if (afterIndex <= 0) {
      return '0'
    }

    return VALID_CHARS[afterIndex - 1]
  }

  // If after string is empty, return next character after 'before'
  if (!after) {
    const beforeFirstChar = before[0]
    const beforeIndex = VALID_CHARS.indexOf(beforeFirstChar)

    if (beforeIndex >= VALID_CHARS.length - 1) {
      return `${before}0`
    }

    return VALID_CHARS[beforeIndex + 1]
  }

  // Find the first differing character position
  let pos = 0
  while (
    pos < before.length &&
    pos < after.length &&
    before[pos] === after[pos]
  ) {
    pos++
  }

  // If one string is a prefix of the other, ensure correct ordering
  if (pos === before.length && pos < after.length) {
    // 'before' is a prefix of 'after', this is fine
    return `${before}0`
  }

  // Get indices in valid characters
  const beforeChar = pos < before.length ? before[pos] : ''
  const afterChar = pos < after.length ? after[pos] : ''
  const beforeIndex = beforeChar ? VALID_CHARS.indexOf(beforeChar) : -1
  const afterIndex = afterChar
    ? VALID_CHARS.indexOf(afterChar)
    : VALID_CHARS.length

  // If there's room between the characters
  if (afterIndex - beforeIndex > 1) {
    const middleIndex = Math.floor((beforeIndex + afterIndex) / 2)
    return before.slice(0, pos) + VALID_CHARS[middleIndex]
  }

  // If no room between characters, append to the before string
  return `${before}0`
}
export const VALID_CHARS =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz'
